import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, DataTable, EstadoVazio, SelectField, fmtBRL, type Column } from "@atlas/ui/web";
import type { ArquivoConsignataria, PreviaArquivoConsignataria } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";

// O ENVIO DO ARQUIVO MENSAL PELO PROPRIO BANCO (#189, fase 2).
//
// O banco sobe o arquivo para um órgão/entidade ONDE TEM VÍNCULO (convênio ativo
// ou rubrica) e vê a prévia do que a confirmação faria. Confirmar é da
// averbadora: por isso a tela não tem botão de confirmar, e diz que aguarda.

const reais = (centavos: number) => fmtBRL(centavos / 100);

type ArquivoDoBanco = ArquivoConsignataria & { orgao: string };

const ESTADO_ROTULO: Record<ArquivoConsignataria["estado"], string> = {
  previa: "Aguardando confirmação da averbadora",
  confirmado: "Confirmado",
  substituido: "Substituído",
};

export function BancoEnvioArquivo() {
  const qc = useQueryClient();
  const [unidadeId, setUnidadeId] = useState("");
  const [arquivoId, setArquivoId] = useState<string | null>(null);

  const orgaosQ = useQuery({ queryKey: ["banco", "arquivos", "orgaos"], queryFn: () => atlas.banco.arquivos.orgaos() });
  const arquivosQ = useQuery({
    queryKey: ["banco", "arquivos", "lista", unidadeId],
    queryFn: () => atlas.banco.arquivos.listar(unidadeId ? { unidadeId: Number(unidadeId) } : {}),
  });
  const previaQ = useQuery({
    queryKey: ["banco", "arquivos", "previa", arquivoId],
    queryFn: () => atlas.banco.arquivos.previa(arquivoId!),
    enabled: arquivoId !== null,
  });

  const enviar = useMutation({
    mutationFn: (file: File) => atlas.banco.arquivos.enviar(Number(unidadeId), file, file.name),
    onSuccess: (r) => {
      if (r.arquivo) {
        setArquivoId(r.arquivo.id);
        qc.setQueryData(["banco", "arquivos", "previa", r.arquivo.id], r);
      }
      void qc.invalidateQueries({ queryKey: ["banco", "arquivos", "lista"] });
    },
  });

  const aoEscolherArquivo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) enviar.mutate(file);
    e.target.value = "";
  };

  const orgaos = orgaosQ.data?.orgaos ?? [];
  const previa: PreviaArquivoConsignataria | null = previaQ.data?.previa ?? null;
  const aberto = previaQ.data?.arquivo ?? null;

  const colunas: Column<ArquivoDoBanco>[] = [
    { key: "competencia", header: "Competência", mono: true, render: (a) => a.competencia },
    { key: "orgao", header: "Órgão/entidade", wrap: true, render: (a) => a.orgao },
    { key: "nome", header: "Arquivo", render: (a) => a.nome },
    { key: "estado", header: "Situação", render: (a) => ESTADO_ROTULO[a.estado] },
    { key: "linhas", header: "Linhas", align: "right", render: (a) => a.linhas },
    { key: "total", header: "Total aceito", align: "right", render: (a) => reais(a.totalCentavos) },
    { key: "enviado", header: "Enviado em", render: (a) => a.criadoEm.slice(0, 16).replace("T", " ") },
  ];

  return (
    <section aria-label="Envio do arquivo mensal" style={{ display: "grid", gap: 16 }}>
      <header>
        <h2 style={{ margin: 0, fontSize: "1.3rem" }}>Enviar arquivo mensal</h2>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 680 }}>
          Envie o arquivo em que a consignatária informa o valor do mês de cada associado, para um órgão/entidade onde você tem
          vínculo. A prévia mostra o que a confirmação faria; quem confirma é a averbadora.
        </p>
      </header>

      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <SelectField
            label="Órgão/entidade"
            value={unidadeId}
            onChange={(e) => { setUnidadeId(e.target.value); setArquivoId(null); }}
            options={[{ value: "", label: "Escolha o órgão/entidade" }, ...orgaos.map((o) => ({ value: String(o.id), label: o.nome }))]}
          />
          <label style={{ display: "grid", gap: 4 }}>
            <span>Arquivo (.txt ou .zip)</span>
            <input
              type="file"
              accept=".txt,.zip"
              aria-label="Arquivo da consignatária"
              disabled={unidadeId === "" || enviar.isPending}
              onChange={aoEscolherArquivo}
            />
          </label>
        </div>
        {orgaosQ.isSuccess && orgaos.length === 0 && (
          <p role="note">Sua consignatária ainda não tem órgão/entidade vinculado para envio.</p>
        )}
        {enviar.isPending && <p role="status">Lendo o arquivo…</p>}
        {enviar.isError && <p role="alert">{mensagemDeErro(enviar.error, "Não foi possível ler o arquivo.")}</p>}
        {enviar.data?.confirmacaoAutomatica?.confirmada === true && (
          <p role="status">Arquivo confirmado automaticamente: os valores já entram no movimento da competência.</p>
        )}
        {enviar.data?.confirmacaoAutomatica?.confirmada === false && (
          <p role="status">
            A confirmação automática não confirmou este arquivo: {enviar.data.confirmacaoAutomatica.motivo} A
            averbadora vai analisar.
          </p>
        )}
      </Card>

      {previa && aberto && (
        <Card>
          <h3 style={{ marginTop: 0 }}>
            Prévia — {aberto.nome} ({aberto.competencia}) · {aberto.orgao}
          </h3>
          <p role="status">{ESTADO_ROTULO[aberto.estado]}.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }} aria-label="Resumo por rubrica">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Rubrica</th>
                <th style={{ textAlign: "right" }}>Aceitas</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Mês anterior</th>
              </tr>
            </thead>
            <tbody>
              {previa.rubricas.map((r) => (
                <tr key={r.rubrica}>
                  <td>{r.rubrica}</td>
                  <td style={{ textAlign: "right" }}>{r.aceitas}</td>
                  <td style={{ textAlign: "right" }}>{reais(r.totalCentavos)}</td>
                  <td style={{ textAlign: "right" }}>{reais(r.totalAnteriorCentavos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {previa.bloqueios.length > 0 && (
            <section aria-label="Pendências para a confirmação">
              <h4>A averbadora precisará de declaração para confirmar</h4>
              <ul>{previa.bloqueios.map((b, i) => <li key={`${b.tipo}-${i}`}>{b.motivo}</li>)}</ul>
            </section>
          )}
          {previa.alertas.length > 0 && (
            <section aria-label="Alertas">
              <h4>Alertas</h4>
              <ul>{previa.alertas.map((a, i) => <li key={`${a.tipo}-${a.matricula}-${a.rubrica}-${i}`}>{a.motivo}</li>)}</ul>
            </section>
          )}
        </Card>
      )}

      <Card>
        <h3 style={{ marginTop: 0 }}>Arquivos enviados</h3>
        <DataTable
          columns={colunas}
          rows={arquivosQ.data?.arquivos ?? []}
          rowKey={(a) => a.id}
          loading={arquivosQ.isLoading}
          onRowClick={(a) => setArquivoId(a.id)}
          emptyState={<EstadoVazio titulo="Nenhum arquivo enviado ainda." />}
        />
      </Card>
    </section>
  );
}
